import { blogPosts, clinic, getBlogPost, getDoctor, getTreatment, treatments } from './site.js';

const defaultImage = '/images/clinic-exterior-real.png';

function absoluteUrl(path = '/') {
  if (/^https?:\/\//i.test(path)) return path;
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${clinic.siteUrl}${normalizedPath}`;
}

function hasConfiguredAddress() {
  return clinic.addressLines.length > 0 && Boolean(clinic.locality);
}

function localizeTitle(title) {
  if (title.includes(clinic.locality)) return title;
  return title.replace(' | My Dentist', ` in ${clinic.locality} | My Dentist`);
}

function localizeDescription(description) {
  if (description.includes(clinic.locality)) return description;
  return `${description.replace(/\.$/, '')} for patients in ${clinic.locality}.`;
}

function organizationSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: clinic.name,
    url: clinic.siteUrl,
    logo: absoluteUrl(clinic.logo),
    description: clinic.description,
    sameAs: clinic.sameAs
  };

  if (clinic.phone) {
    schema.contactPoint = {
      '@type': 'ContactPoint',
      telephone: clinic.phone,
      contactType: 'appointments'
    };
  }

  return schema;
}

function localBusinessSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Dentist',
    name: clinic.name,
    url: clinic.siteUrl,
    image: absoluteUrl('/images/clinic-exterior-real.png'),
    logo: absoluteUrl(clinic.logo),
    description: clinic.description,
    priceRange: clinic.priceRange,
    areaServed: clinic.serviceAreas.map((area) => ({
      '@type': 'City',
      name: area
    })),
    hasMap: clinic.mapUrl,
    sameAs: clinic.sameAs,
    geo: {
      '@type': 'GeoCoordinates',
      latitude: clinic.geo.latitude,
      longitude: clinic.geo.longitude
    }
  };

  if (clinic.phone) schema.telephone = clinic.phone;

  if (hasConfiguredAddress()) {
    schema.address = {
      '@type': 'PostalAddress',
      addressLocality: clinic.locality,
      addressRegion: clinic.region,
      postalCode: clinic.postalCode,
      addressCountry: clinic.country
    };
    if (clinic.streetAddress) schema.address.streetAddress = clinic.streetAddress;
    if (!schema.address.postalCode) delete schema.address.postalCode;
  }

  schema.openingHours = ['Mo 10:00-20:00', 'Tu 10:00-20:00', 'Th 10:00-20:00', 'Fr 10:00-20:00', 'Sa 10:00-20:00', 'Su 10:00-20:00'];

  return schema;
}

function websiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: clinic.name,
    url: clinic.siteUrl,
    inLanguage: 'en-IN'
  };
}

function breadcrumbSchema(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: absoluteUrl(item.path)
    }))
  };
}

function treatmentSchema(treatment) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: treatment.title,
    description: treatment.summary,
    image: absoluteUrl(treatment.image),
    areaServed: clinic.serviceAreas,
    provider: {
      '@type': 'Dentist',
      name: clinic.name,
      url: clinic.siteUrl
    },
    serviceType: treatment.title,
    url: absoluteUrl(`/treatments/${treatment.slug}/`)
  };
}

function blogPostingSchema(post) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: localizeDescription(post.summary),
    articleSection: post.category,
    spatialCoverage: {
      '@type': 'City',
      name: clinic.locality
    },
    datePublished: post.published,
    dateModified: post.updated,
    author: {
      '@type': 'Organization',
      name: 'Clinical team at My Dentist'
    },
    reviewedBy: [
      { '@type': 'Person', name: 'Dr. Mayank Shah' },
      { '@type': 'Person', name: 'Dr. Deepti Shah' }
    ],
    publisher: {
      '@type': 'Organization',
      name: clinic.name,
      logo: {
        '@type': 'ImageObject',
        url: absoluteUrl(clinic.logo)
      }
    },
    mainEntityOfPage: absoluteUrl(`/blog/${post.slug}/`),
    image: absoluteUrl(post.image || defaultImage)
  };
}

function doctorSchema(doctor) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Dentist',
    name: doctor.name,
    url: absoluteUrl(`/dentists/${doctor.slug}/`),
    image: absoluteUrl(doctor.profileImage || defaultImage),
    description: doctor.summary,
    medicalSpecialty: doctor.role,
    memberOf: {
      '@type': 'Dentist',
      name: clinic.name,
      url: clinic.siteUrl
    },
    worksFor: {
      '@type': 'Dentist',
      name: clinic.name,
      url: clinic.siteUrl
    },
    address: hasConfiguredAddress()
      ? {
          '@type': 'PostalAddress',
          streetAddress: clinic.streetAddress,
          addressLocality: clinic.locality,
          addressRegion: clinic.region,
          postalCode: clinic.postalCode,
          addressCountry: clinic.country
        }
      : undefined
  };

  if (clinic.phone) schema.telephone = clinic.phone;
  if (schema.address && !schema.address.postalCode) delete schema.address.postalCode;
  return schema;
}

export function getPageMeta(pathname) {
  const path = pathname.replace(/\/$/, '') || '/';

  if (path === '/') {
    return {
      title: `Dentist in ${clinic.locality} | My Dentist Dental Clinic`,
      description: clinic.description,
      canonicalPath: '/',
      image: defaultImage,
      schemas: [organizationSchema(), localBusinessSchema(), websiteSchema()]
    };
  }

  if (path === '/blog') {
    return {
      title: `Dental Blog for ${clinic.locality} Patients | My Dentist`,
      description:
        `Read practical dental guides from My Dentist for ${clinic.locality} patients, covering check-ups, tooth pain, whitening, implants and child dental care.`,
      canonicalPath: '/blog/',
      image: defaultImage,
      schemas: [
        websiteSchema(),
        breadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Blog', path: '/blog/' }
        ])
      ]
    };
  }

  if (path === '/gallery') {
    return {
      title: `Dental Clinic Gallery in ${clinic.locality} | My Dentist`,
      description:
        `View real clinic photos from ${clinic.name} in ${clinic.locality}, including the exterior, reception, treatment room and dental case gallery.`,
      canonicalPath: '/gallery/',
      image: '/images/clinic-exterior-real.png',
      schemas: [
        websiteSchema(),
        breadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Gallery', path: '/gallery/' }
        ])
      ]
    };
  }

  if (path === '/privacy-policy') {
    return {
      title: `Privacy Policy | ${clinic.name}`,
      description:
        `Read how ${clinic.name} handles appointment form details, patient enquiries and website privacy for patients in ${clinic.locality}.`,
      canonicalPath: '/privacy-policy/',
      image: defaultImage,
      schemas: [
        websiteSchema(),
        breadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Privacy Policy', path: '/privacy-policy/' }
        ])
      ]
    };
  }

  if (path === '/cookie-policy') {
    return {
      title: `Cookie Policy | ${clinic.name}`,
      description:
        `Learn how ${clinic.name} uses essential cookies and browser storage for website functionality and appointment form behaviour.`,
      canonicalPath: '/cookie-policy/',
      image: defaultImage,
      schemas: [
        websiteSchema(),
        breadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Cookie Policy', path: '/cookie-policy/' }
        ])
      ]
    };
  }

  if (path === '/accessibility') {
    return {
      title: `Accessibility | ${clinic.name}`,
      description:
        `Accessibility information for ${clinic.name}, including website usability support and clinic contact options for patients.`,
      canonicalPath: '/accessibility/',
      image: defaultImage,
      schemas: [
        websiteSchema(),
        breadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Accessibility', path: '/accessibility/' }
        ])
      ]
    };
  }

  if (path.startsWith('/dentists/')) {
    const doctor = getDoctor(path.replace('/dentists/', ''));
    if (doctor) {
      return {
        title: `${doctor.name} | ${doctor.role} in ${clinic.locality}`,
        description:
          `Meet ${doctor.name}, ${doctor.role} at ${clinic.name} in ${clinic.locality}. View qualifications, registration details, focus areas and book a consultation.`,
        canonicalPath: `/dentists/${doctor.slug}/`,
        image: doctor.profileImage || defaultImage,
        schemas: [
          doctorSchema(doctor),
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'The Dentist', path: '/#dentist' },
            { name: doctor.name, path: `/dentists/${doctor.slug}/` }
          ])
        ]
      };
    }
  }

  if (path.startsWith('/blog/')) {
    const post = getBlogPost(path.replace('/blog/', ''));
    if (post) {
      return {
        title: localizeTitle(post.metaTitle),
        description: localizeDescription(post.metaDescription),
        canonicalPath: `/blog/${post.slug}/`,
        image: post.image || defaultImage,
        type: 'article',
        schemas: [
          blogPostingSchema(post),
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Blog', path: '/blog/' },
            { name: post.title, path: `/blog/${post.slug}/` }
          ])
        ]
      };
    }
  }

  if (path.startsWith('/treatments/')) {
    const treatment = getTreatment(path.replace('/treatments/', ''));
    if (treatment) {
      return {
        title: localizeTitle(treatment.metaTitle),
        description: localizeDescription(treatment.metaDescription),
        canonicalPath: `/treatments/${treatment.slug}/`,
        image: treatment.image,
        schemas: [
          treatmentSchema(treatment),
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Treatments', path: '/#treatments' },
            { name: treatment.title, path: `/treatments/${treatment.slug}/` }
          ])
        ]
      };
    }
  }

  return {
    title: 'Page Not Found | My Dentist',
    description: `This page could not be found on ${clinic.name}.`,
    canonicalPath: path,
    image: defaultImage,
    schemas: [websiteSchema()]
  };
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function safeJson(value) {
  return JSON.stringify(value).replace(/</g, '\\u003c');
}

export function renderHeadTags(pathname) {
  const meta = getPageMeta(pathname);
  const canonical = absoluteUrl(meta.canonicalPath);
  const image = absoluteUrl(meta.image);
  const schemaTags = meta.schemas
    .map((schema) => `<script type="application/ld+json" data-react-seo="jsonld">${safeJson(schema)}</script>`)
    .join('\n    ');

  return [
    `<title>${escapeHtml(meta.title)}</title>`,
    `<meta name="description" content="${escapeHtml(meta.description)}" />`,
    `<link rel="canonical" href="${escapeHtml(canonical)}" />`,
    `<meta property="og:type" content="${escapeHtml(meta.type || 'website')}" />`,
    `<meta property="og:title" content="${escapeHtml(meta.title)}" />`,
    `<meta property="og:description" content="${escapeHtml(meta.description)}" />`,
    `<meta property="og:url" content="${escapeHtml(canonical)}" />`,
    `<meta property="og:image" content="${escapeHtml(image)}" />`,
    '<meta name="twitter:card" content="summary_large_image" />',
    `<meta name="twitter:title" content="${escapeHtml(meta.title)}" />`,
    `<meta name="twitter:description" content="${escapeHtml(meta.description)}" />`,
    `<meta name="twitter:image" content="${escapeHtml(image)}" />`,
    schemaTags
  ].join('\n    ');
}

export function getTreatmentForPath(pathname) {
  return treatments.find((treatment) => `/treatments/${treatment.slug}` === pathname.replace(/\/$/, ''));
}

export function getPostForPath(pathname) {
  return blogPosts.find((post) => `/blog/${post.slug}` === pathname.replace(/\/$/, ''));
}
